import { Button } from 'components/atoms/Button';
import useCustomerCountry from 'hooks/shared/useCustomerCountry';
import useExecuteWhenElementInView from 'hooks/useExecuteWhenElementInView';
import useFeatures from 'hooks/useFeatures';
import { useState, type FC, type MutableRefObject } from 'react';
import { useTranslation } from 'react-i18next';
import Lottie from 'react-lottie';
import { openInNewTab } from 'utils/helpers';
import { getReviewYears } from '../../helpers';
import tipsAnimationData from '../../tipsIconAimation.json';

const DeductibleExpensesContentNoAI: FC<{
  tips: string[];
}> = ({ tips }) => {
  const { t, i18n } = useTranslation();

  const country = useCustomerCountry();

  const { year } = getReviewYears();

  const [runTipsAnimation, setRunTipsAnimation] = useState(false);

  const deductiblesUrl = useFeatures('expenses.all_you_can_deduct_link_origin')(
    i18n.language,
  );

  const { elementRef } = useExecuteWhenElementInView({
    execute: () => setRunTipsAnimation(true),
  });

  return (
    <div className="flex-1 flex flex-col p-6 bg-expenses-50 border border-expenses-200 rounded-[12px]">
      <h1 className="font-semibold text-xl text-primary mb-7 flex items-center gap-2">
        {t('yearly_review.deductible_expenses_widget.to_be_deducted.title')}
      </h1>
      <div className="flex flex-col flex-1">
        {tips?.length ? (
          tips?.map((tip) => {
            const hasLink = i18n.exists(
              `yearly_review.deductible_expenses_widget.to_be_deducted.tips.${tip}.link.${country}`,
            );

            return (
              <div className="my-2" key={tip}>
                {hasLink ? (
                  <a
                    href={t(
                      `yearly_review.deductible_expenses_widget.to_be_deducted.tips.${tip}.link.${country}`,
                    )}
                    className="text-md font-semibold text-primary hover:text-primary-500 mb-1 block"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t(
                      `yearly_review.deductible_expenses_widget.to_be_deducted.tips.${tip}.title`,
                      { year },
                    )}
                  </a>
                ) : (
                  <p className="text-md font-semibold text-primary mb-1 block">
                    {t(
                      `yearly_review.deductible_expenses_widget.to_be_deducted.tips.${tip}.title`,
                      { year },
                    )}
                  </p>
                )}

                <p className="text-expenses bg-white font-semibold rounded-full px-4 py-1 text-sm inline-block">
                  {t(
                    `yearly_review.deductible_expenses_widget.to_be_deducted.tips.${tip}.subtitle`,
                    { year },
                  )}
                </p>
              </div>
            );
          })
        ) : (
          <div className="min-h-[30vh] flex flex-col items-center justify-center">
            <Lottie
              options={{
                animationData: tipsAnimationData,
                loop: false,
                autoplay: runTipsAnimation,
              }}
              height={75}
              width={75}
            />

            <p className="font-semibold text-primary text-sm mt-3 text-center">
              {t('yearly_review.deductible_expenses_widget.empty_tips_text')}
            </p>
          </div>
        )}

        <div ref={elementRef as MutableRefObject<HTMLDivElement>} />

        <div className="flex flex-col items-center mt-6 gap-4 flex-1 justify-end">
          <p className="text-sm text-primary text-center">
            {t('yearly_review.deductible_expenses_widget.no_ai_text')}
          </p>

          <Button
            color="expenses"
            className="max-w-[60%] mx-auto"
            onClick={() => openInNewTab(deductiblesUrl)}
          >
            {t('yearly_review.deductible_expenses_widget.to_be_deducted.cta')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DeductibleExpensesContentNoAI;
